import {
  CanvasTexture,
  EquirectangularReflectionMapping,
  SRGBColorSpace,
  Texture,
} from "three";
import { colors } from "../ui/theme";

// Stylized equirectangular environment map for the katana's PBR materials —
// the "world" the polished steel mirrors. Metal in three.js gets almost all
// of its surface reflection from the envMap; without one, metalness 0.9 has
// nothing to reflect and the blade reads as flat grey (which the additive
// highlight strips in Katana.tsx used to compensate for by hand).
//
// The content is NOT photoreal — it encodes the dōjō's light story from
// design/Katana Reference.dc.html and Dojo.tsx: near-black night, one warm
// amber andon glow low on one side (with a hot flame speck for the travelling
// glint), a cool moonlight streak high up, faint warm shōji panels at the
// horizon, and a dark warm floor. The renderer PMREM-filters it internally
// (EquirectangularReflectionMapping on a PBR envMap), so it lands as soft
// gradients that sweep along the sori as the blade moves.
//
// Applied per-material on the katana ONLY — never scene.environment — so the
// rest of the scene's look is untouched.

let cached: Texture | null = null;

/**
 * Lazily builds the shared katana env map. Deliberately never disposed: it is
 * a single small texture (256×128 canvas) shared by both hands' katanas,
 * which mount/unmount with wield-mode changes — simpler and safer than
 * refcounting for the lifetime of the app.
 */
export function getKatanaEnvMap(): Texture {
  if (cached) return cached;

  const width = 256;
  const height = 128;
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("2D canvas context unavailable");

  // Base: night sky fading to a warm dark floor (top = up pole).
  const base = ctx.createLinearGradient(0, 0, 0, height);
  base.addColorStop(0, "#161a24");
  base.addColorStop(0.42, "#100c0a");
  base.addColorStop(0.72, "#17100a");
  base.addColorStop(1, "#221507");
  ctx.fillStyle = base;
  ctx.fillRect(0, 0, width, height);

  // Cool moonlight streak, high latitude — matches the #a9c7ff directional
  // light in Dojo.tsx. Kept dim: a cold counterpoint, not a second sun.
  const moon = ctx.createRadialGradient(70, 26, 2, 70, 26, 60);
  moon.addColorStop(0, "rgba(169, 199, 255, 0.5)");
  moon.addColorStop(0.35, "rgba(169, 199, 255, 0.16)");
  moon.addColorStop(1, "rgba(169, 199, 255, 0)");
  ctx.fillStyle = moon;
  ctx.fillRect(0, 0, width, height);

  // Faint warm shōji panels along the horizon — vertical paper rectangles
  // that read as soft structured glints when the blade rotates.
  ctx.fillStyle = "rgba(224, 169, 80, 0.06)";
  for (const x of [18, 122, 232]) {
    ctx.fillRect(x, 52, 14, 26);
  }

  // The andon lantern: the dominant warm mass, low on one side.
  const andon = ctx.createRadialGradient(186, 92, 4, 186, 92, 78);
  andon.addColorStop(0, `${colors.amber}cc`);
  andon.addColorStop(0.3, `${colors.amber}55`);
  andon.addColorStop(1, `${colors.amber}00`);
  ctx.fillStyle = andon;
  ctx.fillRect(0, 0, width, height);

  // Hot flame speck inside the andon — the small near-white core is what
  // produces a crisp travelling specular glint on low-roughness steel.
  const flame = ctx.createRadialGradient(186, 90, 0, 186, 90, 9);
  flame.addColorStop(0, "rgba(255, 242, 208, 0.95)");
  flame.addColorStop(0.5, "rgba(255, 226, 168, 0.4)");
  flame.addColorStop(1, "rgba(255, 226, 168, 0)");
  ctx.fillStyle = flame;
  ctx.fillRect(0, 0, width, height);

  const texture = new CanvasTexture(canvas);
  texture.mapping = EquirectangularReflectionMapping;
  texture.colorSpace = SRGBColorSpace;
  texture.needsUpdate = true;
  cached = texture;
  return texture;
}
